import type { Tool, ToolPostPayload } from "@repo/app-types/entities/tool";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import {
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import type { FC } from "react";
import { Dialog } from "react-aria-components";
import {
  useGetToolQuery,
  useUpdateToolMutation,
} from "../../../api/entities/tool.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { ToolEditorSlideoverDialogContent } from "./toolEditorSlideoverDialogContent.js";

export interface ToolEditorSlideoverDialogProps {
  toolId: string;
  onRemoveFromAgent: (toolId: string) => void;
  onAfterSave?: (updatedTool: Tool) => Promise<void>;
}

export const ToolEditorSlideoverDialog: FC<ToolEditorSlideoverDialogProps> = (
  props,
) => {
  const { data, isLoading, isError } = useGetToolQuery(props.toolId);

  const [updateTool] = useUpdateToolMutation();

  const handleRemoveFromAgent = (toolId: string, close: () => void) => {
    props.onRemoveFromAgent(toolId);
    close();
  };

  const handleSave = async (payload: ToolPostPayload, close: () => void) => {
    try {
      const { tool: updatedTool } = await updateTool({
        toolId: props.toolId,
        payload,
      }).unwrap();

      showToast({
        variant: ToastVariant.Success,
        title: "Tool updated",
        description:
          "Tool has been updated successfully. Publishing changes...",
      });

      try {
        await props.onAfterSave?.(updatedTool);
      } catch (error) {
        console.error("Failed to publish after saving tool:", error);
      }

      close();
    } catch (error) {
      showToast({
        variant: ToastVariant.Error,
        title: "Failed to update tool",
        description: getApiErrorMessage(error, "Please try again."),
      });

      // Rethrow so the form keeps the user's changes
      throw error;
    }
  };

  const title = data ? `Edit ${data.tool.name}` : "Edit tool";

  return (
    <Dialog className="isolate flex h-full flex-col">
      {({ close }) => (
        <>
          <SlideoverDialogTitleHeader isLoading={isLoading}>
            {title}
          </SlideoverDialogTitleHeader>

          {!data && isLoading ? (
            <SlideoverDialogContent>
              <div className="p-4">
                <DefaultSkeleton />
              </div>
            </SlideoverDialogContent>
          ) : null}

          {!data && !isLoading && isError ? (
            <SlideoverDialogContent>
              <div className="p-4">
                <p className="text-sm text-red-500">
                  Failed to load tool. Please try again.
                </p>
              </div>
            </SlideoverDialogContent>
          ) : null}

          {data ? (
            // Keyed by tool, so unsaved edits never carry over if the
            // slideover is pointed at another tool without closing.
            <ToolEditorSlideoverDialogContent
              key={data.tool.id}
              tool={data.tool}
              secrets={data.secrets}
              onRemoveFromAgent={(toolId) =>
                handleRemoveFromAgent(toolId, close)
              }
              onCancel={close}
              onSave={(payload) => handleSave(payload, close)}
            />
          ) : null}
        </>
      )}
    </Dialog>
  );
};
